import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaArrowRight,
  FaRocket, 
  FaHandshake,
  FaGraduationCap,
  FaChartLine,
  FaLightbulb,
  FaHandHoldingUsd, 
  FaFileAlt,
  FaBriefcase,
  FaCog, 
  FaGlobe,
  FaArrowDown,
  FaTimes
} from 'react-icons/fa';
import SEO from '../components/SEO/SEO';
import contentData from '../data/contentData.json';
import Banner2Image from './Banner2.jpg';

const StartupPage = () => {
  const navigate = useNavigate();
  const [activeObjective, setActiveObjective] = useState(null);
  const [arrowPosition, setArrowPosition] = useState(0);
  const [isAutoAnimating, setIsAutoAnimating] = useState(false);
  const [activeNav, setActiveNav] = useState('home');
  const objectivesRef = useRef([]);
  const sectionRef = useRef(null);
  const animationTimeoutRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  // SEO data for Startup page
  const seoData = {
    title: "Empowering India's Innovation Ecosystem - Startup Support | Dhyon Technologies",
    description: `Dhyon Technologies partners with forward-thinking organisations to accelerate startups, research, and sustainable innovation—driving measurable impact toward the vision of Viksit Bharat 2047.`,
    keywords: "Startup Support, Innovation Ecosystem, FICCI Mercedes-Benz, Bharat Innovation, Startup Mentoring, Viksit Bharat 2047"
  };

  // Key Objectives data
  const objectives = [
    {
      id: 1,
      title: "Accelerating Lab-to-Market Translation",
      description: "Dhyon Technologies enables the seamless transition of research, prototypes, and emerging technologies into scalable, market-ready solutions that address real-world industry and societal challenges.",
      icon: <FaRocket />,
      image: "/assets/key1.png"
    },
    {
      id: 2,
      title: "Strengthening Industry–Academia Collaboration",
      description: "We foster structured collaboration between startups, academic institutions, industry leaders, and policymakers to drive innovation, knowledge exchange, and co-creation of impactful solutions.",
      icon: <FaHandshake />,
      image: "/assets/key2.png"
    },
    {
      id: 3,
      title: "Advancing Innovation and Entrepreneurship",
      description: "Dhyon supports entrepreneurs and innovators through strategic mentorship, technology enablement, and ecosystem partnerships to promote sustainable economic growth and job creation.",
      icon: <FaChartLine />,
      image: "/assets/key3.png"
    },
    {
      id: 4,
      title: "Facilitating Access to Government Grants and Incentive Programs",
      description: "We assist innovators in identifying, evaluating, and securing government grants, incentive schemes, and policy-backed funding aligned with national development priorities.",
      icon: <FaHandHoldingUsd />,
      image: "/assets/key4.png"
    },
    {
      id: 5,
      title: "Enabling Investor Access and Capital Readiness",
      description: "Dhyon connects startups with angel investors, venture capital firms, and institutional funding networks while preparing them for due diligence, valuation readiness, and investment engagement.",
      icon: <FaBriefcase />,
      image: "/assets/key5.png"
    },
    {
      id: 6,
      title: "Investor Pitch Deck Development and Storytelling",
      description: "We support startups in crafting compelling, investor-ready pitch decks that clearly communicate vision, value proposition, market opportunity, traction, and growth strategy.",
      icon: <FaFileAlt />,
      image: "/assets/key6.png"
    },
    {
      id: 7,
      title: "Product Requirements Document (PRD) Development",
      description: "Dhyon assists innovators in defining clear, structured Product Requirement Documents (PRDs) that translate ideas into actionable technical and business specifications aligned with market needs.",
      icon: <FaCog />,
      image: "/assets/key7.png"
    },
    {
      id: 8,
      title: "Business Requirements Document (BRD) Development",
      description: "We help organizations articulate business objectives, stakeholder needs, functional requirements, and success metrics through well-structured BRDs to guide development and execution.",
      icon: <FaBriefcase />,
      image: "/assets/key8.png"
    },
    {
      id: 9,
      title: "Minimum Viable Product (MVP) Design & Development",
      description: "Dhyon enables rapid MVP creation through agile methodologies, enabling startups to validate concepts, gather user feedback, and accelerate go-to-market strategies.",
      icon: <FaLightbulb />,
      image: "/assets/key9.png"
    },
    {
      id: 10,
      title: "Aligning with India's Vision for Viksit Bharat 2047",
      description: "All initiatives are strategically aligned with India's long-term development goals, fostering innovation-led growth, technological self-reliance, and global competitiveness.",
      icon: <FaGlobe />,
      image: "/assets/key10.png"
    }
  ];

  // Auto-animate through objectives
  useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isAutoAnimating) {
            startAutoAnimation();
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(sectionRef.current);

    return () => {
      observer.disconnect();
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [isAutoAnimating]);

  const startAutoAnimation = () => {
    setIsAutoAnimating(true);
    let currentIndex = 0;
    
    const animateNext = () => {
      if (currentIndex < objectives.length) {
        // Calculate arrow position based on the objective's position
        if (objectivesRef.current[currentIndex] && sectionRef.current) {
          const element = objectivesRef.current[currentIndex];
          const rect = element.getBoundingClientRect();
          const sectionRect = sectionRef.current.getBoundingClientRect();
          const relativeTop = rect.top - sectionRect.top + rect.height / 2;
          setArrowPosition(relativeTop);

          // Smooth scroll to keep objective in view
          const elementTop = element.offsetTop;
          const elementHeight = element.offsetHeight;
          const windowHeight = window.innerHeight;
          const scrollPosition = window.scrollY;
          const elementVisibleTop = elementTop - scrollPosition;
          
          // Only scroll if element is not well visible
          if (elementVisibleTop < 100 || elementVisibleTop + elementHeight > windowHeight - 100) {
            element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
          }
        }

        // Set active objective after arrow position is calculated
        setTimeout(() => {
          setActiveObjective(objectives[currentIndex].id);
        }, 400);

        currentIndex++;
        
        if (currentIndex < objectives.length) {
          animationTimeoutRef.current = setTimeout(animateNext, 4000); // Wait 4 seconds before moving to next
        } else {
          // Loop back to start after completing all objectives
          setTimeout(() => {
            setIsAutoAnimating(false);
            // Restart after a pause
            setTimeout(() => {
              if (sectionRef.current) {
                startAutoAnimation();
              }
            }, 3000);
          }, 4000);
        }
      }
    };

    // Start animation after a short delay
    animationTimeoutRef.current = setTimeout(() => {
      animateNext();
    }, 1500);
  };

  const handleObjectiveClick = (id, index) => {
    // Stop auto-animation when user interacts
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      setIsAutoAnimating(false);
    }
    
    setActiveObjective(id);
    
    if (objectivesRef.current[index] && sectionRef.current) {
      const element = objectivesRef.current[index];
      const rect = element.getBoundingClientRect();
      const sectionRect = sectionRef.current.getBoundingClientRect();
      const relativeTop = rect.top - sectionRect.top + rect.height / 2;
      setArrowPosition(relativeTop);
    }
  };

  // Smooth scroll function
  const scrollToSection = (sectionId) => {
    if (sectionId === 'home') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      setActiveNav('home');
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 160; // Account for fixed header + nav bar
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveNav(sectionId);
    }
  };

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'key-objectives', 'program-timeline'];
      const scrollPosition = window.scrollY + 200;

      // Check if at top
      if (window.scrollY < 100) {
        setActiveNav('home');
        return;
      }

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i]);
        if (element) {
          const offsetTop = element.offsetTop;
          if (scrollPosition >= offsetTop) {
            setActiveNav(sections[i]);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <SEO {...seoData} />
      
      {/* First Section - Banner Image */}
      <section className="relative w-full overflow-hidden bg-white">
        <div className="relative w-full" style={{ height: '150px' }}>
          <motion.img 
            src="/assets/banner1.png" 
            alt="Innovation Ecosystem Banner"
            className="w-full h-full object-cover"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          />
          {/* Golden Text Overlay */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <h1 
              className="text-4xl md:text-6xl lg:text-7xl font-bold text-center"
              style={{
                color: '#FFD700',
                textShadow: '0 4px 20px rgba(255, 215, 0, 0.5), 0 2px 10px rgba(0, 0, 0, 0.8)',
                letterSpacing: '0.05em'
              }}
            >
              Startup India
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Sticky Navigation Bar */}
      <nav className="sticky top-[76px] z-40 bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-center gap-3 md:gap-6 lg:gap-8 py-3 overflow-x-auto">
            <motion.button
              onClick={() => scrollToSection('home')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`
                font-semibold text-sm md:text-base transition-all duration-300 relative px-5 py-2.5 rounded-lg whitespace-nowrap
                ${activeNav === 'home' 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-gray-700 hover:text-blue-600'
                }
              `}
            >
              Home
              {activeNav === 'home' && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600"
                  layoutId="activeNavIndicator"
                  initial={false}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
            </motion.button>
            
            <motion.button
              onClick={() => scrollToSection('about')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`
                font-semibold text-sm md:text-base transition-all duration-300 relative px-5 py-2.5 rounded-lg whitespace-nowrap
                ${activeNav === 'about' 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-gray-700 hover:text-blue-600'
                }
              `}
            >
              About
              {activeNav === 'about' && (
          <motion.div
                  className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600"
                  layoutId="activeNavIndicator"
                  initial={false}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
            </motion.button>
            
            <motion.button
              onClick={() => scrollToSection('key-objectives')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`
                font-semibold text-sm md:text-base transition-all duration-300 relative px-5 py-2.5 rounded-lg whitespace-nowrap
                ${activeNav === 'key-objectives' 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-gray-700 hover:text-blue-600'
                }
              `}
            >
              Key Objectives
              {activeNav === 'key-objectives' && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600"
                  layoutId="activeNavIndicator"
                  initial={false}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
            </motion.button>
            
            <motion.button
              onClick={() => scrollToSection('program-timeline')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`
                font-semibold text-sm md:text-base transition-all duration-300 relative px-5 py-2.5 rounded-lg whitespace-nowrap
                ${activeNav === 'program-timeline' 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-gray-700 hover:text-blue-600'
                }
              `}
            >
              Startup Nurturing & Funding Process
              {activeNav === 'program-timeline' && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600"
                  layoutId="activeNavIndicator"
                  initial={false}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
            </motion.button>
            
            <motion.button
              onClick={() => navigate('/investor-insights')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`
                font-semibold text-sm md:text-base transition-all duration-300 relative px-5 py-2.5 rounded-lg whitespace-nowrap
                text-gray-700 hover:text-blue-600
              `}
            >
              Investor Insights
            </motion.button>
        </div>
        </div>
      </nav>

      {/* Banner Image Section - Above Empowering India's Section */}
      <section className="relative w-full overflow-hidden bg-white">
        <motion.img 
          src={Banner2Image} 
          alt="Startup India Banner"
          className="w-full h-auto object-cover"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        />
      </section>

      {/* Second Section - Empowering India's Innovation Ecosystem Content */}
      <section id="about" className="py-16 md:py-24 bg-gradient-to-b from-white via-blue-50/30 to-white relative overflow-hidden scroll-mt-24">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl"></div>
        </div>

        <div className="container px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Header Section */}
            <div className="text-center mb-12 md:mb-16">
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 mb-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-lg"
            >
                <FaRocket className="text-white text-3xl md:text-4xl" />
            </motion.div>
            
              <motion.h1 
                className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
                  Empowering India's Innovation Ecosystem
                </span>
              </motion.h1>
              
              <motion.div
                className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mb-8 rounded-full"
                initial={{ width: 0 }}
                whileInView={{ width: 96 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }}
              />
              
              <motion.p 
                className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed font-semibold max-w-4xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                Dhyon Technologies partners with forward-thinking organisations to accelerate startups, research, and sustainable innovation—driving measurable impact toward the vision of <span className="text-blue-600 font-bold">Viksit Bharat 2047</span>.
              </motion.p>
            </div>

            {/* Content Cards */}
              <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="space-y-6"
            >
              {/* Card 1 */}
              <motion.div
                className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                whileHover={{ y: -5 }}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FaRocket className="text-blue-600 text-xl" />
                  </div>
                  <p className="text-base md:text-lg text-gray-700 leading-relaxed flex-1">
                    Anchored by Dhyon Technologies, this initiative is designed to empower early-stage and growth-stage startups, innovators, and solution builders who are addressing India's emerging economic, social, and sustainability challenges through technology and innovation.
                  </p>
                </div>
              </motion.div>
              
              {/* Card 2 */}
              <motion.div
                className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                whileHover={{ y: -5 }}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <FaChartLine className="text-purple-600 text-xl" />
                  </div>
                  <p className="text-base md:text-lg text-gray-700 leading-relaxed flex-1">
                    The program envisions a thriving innovation ecosystem across Bharat, where young minds, startups, researchers, and academic institutions transform real-world challenges into scalable, impact-driven solutions. By nurturing purpose-led entrepreneurship and deep-tech innovation, Dhyon Technologies actively contributes to India's journey toward becoming a <span className="text-purple-600 font-semibold">Viksit Bharat by 2047</span>.
                  </p>
                </div>
              </motion.div>

              {/* Card 3 */}
              <motion.div
                className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                whileHover={{ y: -5 }}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                    <FaHandshake className="text-cyan-600 text-xl" />
                  </div>
                  <p className="text-base md:text-lg text-gray-700 leading-relaxed flex-1">
                    Participants gain access to Dhyon's innovation ecosystem, including expert mentorship, technology enablement, incubation support, and strategic guidance from industry leaders. The program also creates opportunities for innovators to showcase their solutions across national platforms, industry forums, and collaborative ecosystems curated by Dhyon Technologies.
                  </p>
                </div>
              </motion.div>
              
              {/* Card 4 */}
              <motion.div
                className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 md:p-8 shadow-lg border-2 border-blue-200 hover:shadow-xl transition-all duration-300"
                whileHover={{ y: -5, scale: 1.01 }}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                    <FaGlobe className="text-white text-xl" />
                  </div>
                  <p className="text-base md:text-lg text-gray-700 leading-relaxed flex-1 font-medium">
                    Through focused support, ecosystem partnerships, and hands-on guidance, Dhyon Technologies enables innovators to access visibility, validation, funding pathways, and long-term growth opportunities. Rooted in a commitment to innovation, sustainability, and digital transformation, Dhyon continues to develop and accelerate impactful solutions that enhance India's global competitiveness and future readiness.
                  </p>
            </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Key Objectives with Animation */}
      <section 
        id="key-objectives"
        ref={sectionRef}
        className="py-20 bg-gray-50 relative overflow-hidden scroll-mt-24"
      >
        <div className="container px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12 md:mb-16 text-center"
          >
            <motion.div
              className="flex items-center justify-center gap-2 mb-4"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
              <div className="w-3 h-3 bg-blue-600 rounded"></div>
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wide">HIGHLIGHTS</span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Key Objectives
            </h2>
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              This Startup India initiative is strategically designed to catalyse innovation-driven growth across Bharat by focusing on the following core objectives:
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            {/* Objectives List - Full Width */}
            <div className="relative">
              <div className="relative">
                  {/* Objectives List - Full Vertical List */}
                  <div className="space-y-3">
                    {objectives.map((objective, index) => (
                      <motion.div
                        key={objective.id}
                        ref={(el) => {
                          if (el) objectivesRef.current[index] = el;
                        }}
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        className="relative"
                      >
                <motion.div
                          className={`
                            relative bg-blue-600 rounded-xl shadow-lg cursor-pointer overflow-hidden transition-all duration-300
                            ${activeObjective === objective.id 
                              ? 'bg-blue-700 shadow-2xl border-2 border-blue-400 scale-[1.02]' 
                              : 'hover:bg-blue-700 hover:shadow-xl border-2 border-transparent'
                            }
                          `}
                          onMouseEnter={() => {
                            // Clear any pending close timeout
                            if (hoverTimeoutRef.current) {
                              clearTimeout(hoverTimeoutRef.current);
                            }
                            handleObjectiveClick(objective.id, index);
                          }}
                          onMouseLeave={() => {
                            // Close image when mouse leaves the objective with a small delay
                            hoverTimeoutRef.current = setTimeout(() => {
                              setActiveObjective(null);
                            }, 200);
                          }}
                          onClick={() => handleObjectiveClick(objective.id, index)}
                          whileHover={{ scale: 1.01, x: 5 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <div className="relative p-5 md:p-6">
                            <div className="flex items-center gap-5">
                              {/* Icon */}
                <motion.div
                                className={`
                                  flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center transition-all duration-300
                                  ${activeObjective === objective.id
                                    ? 'bg-white text-blue-600 scale-110'
                                    : 'bg-white/20 text-white'
                                  }
                                `}
                                animate={{
                                  scale: activeObjective === objective.id ? [1, 1.1, 1] : 1
                                }}
                                transition={{ 
                                  duration: 0.5,
                                  repeat: activeObjective === objective.id ? Infinity : 0,
                                  repeatDelay: 2
                                }}
                >
                                <span className="text-xl md:text-2xl">
                                  {objective.icon}
                                </span>
                              </motion.div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <h3 className="text-base md:text-lg font-semibold text-white leading-snug">
                                  {index + 1}. {objective.title}
                                </h3>
                              </div>
                            </div>
                      </div>
                        </motion.div>
              </motion.div>
                          ))}
          </div>
                      </div>
                    </div>

            {/* Centered Image Popup Modal */}
            <AnimatePresence>
              {activeObjective && (() => {
                const activeObj = objectives.find(obj => obj.id === activeObjective);
                return activeObj && activeObj.image && (
                <motion.div
                    key={activeObjective}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Backdrop */}
                    <motion.div
                      className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    />

                    {/* Image Container - Smaller Size */}
                      <motion.div
                      className="relative z-10 max-w-2xl w-full bg-white rounded-xl shadow-2xl overflow-hidden pointer-events-auto"
                      initial={{ scale: 0.9, opacity: 0, y: 30 }}
                      animate={{ scale: 1, opacity: 1, y: 0 }}
                      exit={{ scale: 0.9, opacity: 0, y: 30 }}
                      transition={{ 
                        duration: 0.4,
                        ease: [0.34, 1.56, 0.64, 1]
                      }}
                      onMouseEnter={() => {
                        // Clear timeout when hovering over modal
                        if (hoverTimeoutRef.current) {
                          clearTimeout(hoverTimeoutRef.current);
                        }
                      }}
                      onMouseLeave={() => {
                        // Close when leaving modal
                        hoverTimeoutRef.current = setTimeout(() => {
                          setActiveObjective(null);
                        }, 200);
                      }}
                    >
                      {/* Image Section - Reduced Size */}
                      <div className="h-[40vh] bg-gradient-to-br from-blue-50 via-purple-50 to-blue-50 flex items-center justify-center p-6">
                        <motion.img 
                          src={activeObj.image} 
                          alt={activeObj.title}
                          className="max-w-full max-h-full object-contain"
                          initial={{ scale: 0.9, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ 
                            duration: 0.4,
                            delay: 0.1,
                            ease: [0.34, 1.56, 0.64, 1]
                          }}
                        />
                      </div>
                      
                      {/* Content Section */}
                      <motion.div
                        className="p-5 bg-white"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.2 }}
                      >
                        <div className="flex items-center gap-3 mb-3">
              <motion.div
                            className="text-3xl text-blue-600"
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ 
                              duration: 0.4,
                              delay: 0.3,
                              type: "spring",
                              stiffness: 200
                            }}
                          >
                            {activeObj.icon}
                      </motion.div>
                          <motion.h3
                            className="text-xl font-bold text-gray-900 flex-1"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.4 }}
                          >
                            {activeObj.title}
                          </motion.h3>
                    </div>
                        <motion.p
                          className="text-sm text-gray-700 leading-relaxed"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3, delay: 0.5 }}
                        >
                          {activeObj.description}
                        </motion.p>
                </motion.div>
                    </motion.div>
                </motion.div>
                );
              })()}
          </AnimatePresence>
            {/* Centered Apply Now Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-16 text-center"
            >
              <a
                href="https://docs.google.com/forms/d/1V7zbbBlxZvzPbzjW6Yo0mVMz-Ab7aTSuqeGr67ngYg0/edit?usp=forms_home&ouid=113987775710733955459&ths=true"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-10 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-black rounded-2xl shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 text-lg group"
              >
                Apply Now
              </a>
            </motion.div>
          </div>

        </div>
      </section>

      {/* Startup Nurturing & Funding Process Section */}
      <section id="program-timeline" className="py-20 bg-white scroll-mt-24">
        <div className="container px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 text-center md:text-left leading-tight">
                Startup Nurturing & Funding Process
              </h2>
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="https://docs.google.com/forms/d/1V7zbbBlxZvzPbzjW6Yo0mVMz-Ab7aTSuqeGr67ngYg0/edit?usp=forms_home&ouid=113987775710733955459&ths=true"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-3.5 bg-blue-600 text-white font-bold rounded-xl shadow-lg hover:bg-blue-700 transition-all text-sm whitespace-nowrap"
              >
                Apply Now
              </motion.a>
            </div>

          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {/* Card 1: Ecosystem Onboarding & Discovery */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg mb-4">
                  1
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  Ecosystem Onboarding & Discovery
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4 italic">
                  Build the foundation for innovation
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We onboard high-potential startups into Dhyon's innovation ecosystem, connecting them with mentors, institutions, and industry leaders to align ideas with real-world challenges.
                </p>
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Key Focus:</p>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Startup onboarding & orientation</li>
                    <li>• Ecosystem & partner integration</li>
                    <li>• Alignment with national innovation goals</li>
                  </ul>
                </div>
              </motion.div>

              {/* Card 2: Idea Validation & Market Fit */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-purple-600 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg mb-4">
                  2
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  Idea Validation & Market Fit
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4 italic">
                  Transform ideas into viable opportunities
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We help founders validate problems, refine solutions, and identify strong market demand through structured research and expert guidance.
                </p>
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Key Focus:</p>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Problem–solution validation</li>
                    <li>• Market research & user insights</li>
                    <li>• Business model development</li>
                  </ul>
                </div>
              </motion.div>

              {/* Card 3: Product & Technology Enablement */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg mb-4">
                  3
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  Product & Technology Enablement
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4 italic">
                  From concept to scalable solution
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Startups receive hands-on support to design, build, and refine scalable products using modern technologies and agile methodologies.
                </p>
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Key Focus:</p>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• MVP & prototype development</li>
                    <li>• Technology architecture & scalability</li>
                    <li>• Product-market alignment</li>
                  </ul>
                </div>
              </motion.div>

              {/* Card 4: Growth Acceleration & Investment Readiness */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg mb-4">
                  4
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  Growth Acceleration & Investment Readiness
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4 italic">
                  Preparing startups for scale and funding
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We equip startups with the strategic tools, storytelling, and financial clarity needed to attract investors and enterprise partners.
                </p>
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Key Focus:</p>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Go-to-market strategy</li>
                    <li>• Pitch deck & investor readiness</li>
                    <li>• Revenue & growth modeling</li>
                  </ul>
                </div>
              </motion.div>

              {/* Card 5: Funding Access & National Visibility */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg mb-4">
                  5
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  Funding Access & National Visibility
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4 italic">
                  Connecting innovation to opportunity
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Startups gain access to funding avenues and national platforms to accelerate growth and long-term sustainability.
                </p>
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Key Focus:</p>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• CSR, government & institutional funding</li>
                    <li>• Corporate partnerships</li>
                    <li>• National & global showcase opportunities</li>
                  </ul>
                </div>
              </motion.div>

              {/* Card 6: AWS Credits & Cloud Support for Startups */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg mb-4">
                  6
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  AWS Credits & Cloud Support for Startups
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4 italic">
                  Empowering startups to scale faster
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We empower startups to scale faster by providing AWS Cloud Credits and end-to-end cloud enablement support. Through our startup ecosystem partnerships, eligible founders can access AWS credits to reduce infrastructure costs, accelerate product development, and experiment freely without early financial pressure.
                </p>
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Key Focus:</p>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• AWS Cloud Credits access</li>
                    <li>• Cloud architecture planning & deployment</li>
                    <li>• Optimisation, security & cost management</li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(-45deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:30px_30px]"></div>
        
        <div className="container relative z-10 px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Join the Innovation Ecosystem?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Let's discuss how we can support your startup journey and contribute to India's vision of Viksit Bharat 2047.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  <span>Get Started Today</span>
                  <FaArrowRight className="ml-2 transform group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default StartupPage;
